import { useCallback, useState } from 'react'
import { css, keyframes } from '@emotion/react'
import { CircularProgress } from '@mui/material'
import { motion } from 'framer-motion'
import { createPortal } from 'react-dom'
import { isElementOutOfViewport } from '@utils/isElementOutOfViewport'
import {
  DISTANCE_FROM_TARGET,
  MODAL_HEIGHT,
  fadeInAnimationVariants,
  getBaseModalCss,
} from './styles'
import { ImagePreviewModalProps } from './types'
import { t } from '@lingui/core/macro'

/**
 * Google Drive URLs need to be modified to be able to be embedded in an iframe.
 * This function takes a Google Drive URL and returns a URL that can be used in an iframe.
 *
 * @param url
 * @returns string
 *
 * @example
 * getGoogleDriveAssetUrl('https://drive.usercontent.google.com/download?id=1RWWWfyzWRaZKMcvPG3onRnvOMocTXuerdq')
 *
 * // returns
 *
 * 'https://drive.google.com/file/d/1RWWWfyzWRaZKMcvPG3onRnvOMocTXuerdq/preview'
 *
 */
function getGoogleDriveAssetUrl(url: string) {
  const urlObject = new URL(url)
  const assetId = urlObject.searchParams.get('id') || ''
  const googleDrivePreviewUrl = 'https://drive.google.com/file/d/ASSET_ID/preview'
  return googleDrivePreviewUrl.replace('ASSET_ID', assetId)
}

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

export const ImagePreviewModal = ({
  top,
  left,
  bottom,
  imageUrl,
  mediaType,
  container = document.body,
}: ImagePreviewModalProps) => {
  const [isImageLoading, setIsImageLoading] = useState(true)
  const shouldModalOpenUp = isElementOutOfViewport({
    innerElementHeight: MODAL_HEIGHT,
    top,
    bottom,
  })

  // when modal needs to open up, we increase its distance from target to look better
  const topPosition = shouldModalOpenUp
    ? bottom - (DISTANCE_FROM_TARGET + 15) - MODAL_HEIGHT
    : bottom + DISTANCE_FROM_TARGET

  const onLoad = useCallback(() => {
    setIsImageLoading(false)
  }, [])

  if (!imageUrl) return null

  return createPortal(
    <motion.div
      variants={fadeInAnimationVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      css={css`
        top: ${topPosition}px;
        left: ${left}px;
        ${getBaseModalCss(shouldModalOpenUp)}
      `}
    >
      {isImageLoading && (
        <CircularProgress size={30} color="inherit" sx={{ position: 'absolute', color: 'white' }} />
      )}
      <RenderAsset imageUrl={imageUrl} mediaType={mediaType} onLoad={onLoad} />
    </motion.div>,
    container,
  )
}

function RenderAsset({
  imageUrl,
  mediaType,
  onLoad,
}: Pick<ImagePreviewModalProps, 'imageUrl' | 'mediaType'> & { onLoad: () => void }) {
  const isVideo = imageUrl.includes('youtu.be')
  if (isVideo) return <Video videoUrl={imageUrl} onLoad={onLoad} />

  const isGoogleDriveImage = imageUrl.includes('drive')
  if (isGoogleDriveImage) return <GoogleDriveAssetPreview imageUrl={imageUrl} onLoad={onLoad} />

  // if mediaType is not provided, it means image is not base64 encoded
  if (!mediaType) return <Image imageUrl={imageUrl} onLoad={onLoad} />

  return <Base64Image imageUrl={imageUrl} mediaType={mediaType} onLoad={onLoad} />
}

function Video({ videoUrl, onLoad }: { videoUrl: string; onLoad: () => void }) {
  const youtubeEmbedUrl = `${videoUrl.replace(
    'youtu.be',
    'youtube.com/embed',
  )}?rel=0&modestbranding=1&autohide=1&mute=1&showinfo=0&controls=0&autoplay=1`

  return (
    <iframe
      width="100%"
      height="100%"
      src={youtubeEmbedUrl}
      allow="autoplay"
      allowFullScreen
      onLoad={onLoad}
      title={t`YouTube video player`}
      style={{
        borderRadius: '6px',
      }}
    />
  )
}

function Image({
  imageUrl,
  onLoad,
}: Pick<ImagePreviewModalProps, 'imageUrl'> & { onLoad: () => void }) {
  const hasHostname = imageUrl.includes('http://') || imageUrl.includes('https://')
  // The mediaType is delayed being fulfilled in the parent component. So we simulate a loading spinner
  // until the mediaType is available; but to differentiate that from a normal image, we check if the
  // hostname is missing to indicate that it's a base64 image.
  const animateWhileBase64Loads =
    !hasHostname &&
    css`
      animation: ${spin} 1s ease infinite;
    `

  return (
    <img
      src={
        hasHostname
          ? imageUrl
          : 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiNmZmZmZmYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBjbGFzcz0ibHVjaWRlIGx1Y2lkZS1sb2FkZXItY2lyY2xlIj48cGF0aCBkPSJNMjEgMTJhOSA5IDAgMSAxLTYuMjE5LTguNTYiLz48L3N2Zz4='
      }
      alt={t`Preview`}
      style={{
        maxHeight: '100%',
        maxWidth: '100%',
        borderRadius: '6px',
        objectFit: 'contain',
      }}
      css={animateWhileBase64Loads}
      onLoad={onLoad}
      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.target as HTMLImageElement
        img.onerror = null
        img.src =
          'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHZpZXdCb3g9IjAgMCA4OCA4OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTcuMzMzMjUgNy4zMzMyNUw4MC42NjY2IDgwLjY2NjYiIHN0cm9rZT0iIzkzRDVFNSIgc3Ryb2tlLXdpZHRoPSI0IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHBhdGggZD0iTTM4LjE3IDM4LjE2OTlDMzcuNDg4NyAzOC44NTEyIDM2LjY3OTggMzkuMzkxNyAzNS43ODk2IDM5Ljc2MDRDMzQuODk5NCA0MC4xMjkyIDMzLjk0NTMgNDAuMzE5IDMyLjk4MTcgNDAuMzE5QzMyLjAxODEgNDAuMzE5IDMxLjA2NCA0MC4xMjkyIDMwLjE3MzggMzkuNzYwNEMyOS4yODM2IDM5LjM5MTcgMjguNDc0NyAzOC44NTEyIDI3Ljc5MzQgMzguMTY5OUMyNy4xMTIgMzcuNDg4NSAyNi41NzE2IDM2LjY3OTcgMjYuMjAyOCAzNS43ODk1QzI1LjgzNDEgMzQuODk5MiAyNS42NDQzIDMzLjk0NTEgMjUuNjQ0MyAzMi45ODE1QzI1LjY0NDMgMzIuMDE4IDI1LjgzNDEgMzEuMDYzOSAyNi4yMDI4IDMwLjE3MzZDMjYuNTcxNiAyOS4yODM0IDI3LjExMiAyOC40NzQ2IDI3Ljc5MzQgMjcuNzkzMiIgc3Ryb2tlPSIjOTNENUU1IiBzdHJva2Utd2lkdGg9IjQiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8cGF0aCBkPSJNNDkuNSA0OS41TDIyIDc3IiBzdHJva2U9IiM5M0Q1RTUiIHN0cm9rZS13aWR0aD0iNCIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxwYXRoIGQ9Ik02NiA0NEw3NyA1NSIgc3Ryb2tlPSIjOTNENUU1IiBzdHJva2Utd2lkdGg9IjQiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8cGF0aCBkPSJNMTMuMTYzMyAxMy4xNjMzQzEyLjQ3OTggMTMuODQgMTEuOTM2OCAxNC42NDUxIDExLjU2NTUgMTUuNTMyNEMxMS4xOTQyIDE2LjQxOTYgMTEuMDAyMSAxNy4zNzE1IDExIDE4LjMzMzNWNjkuNjY2N0MxMSA3MS42MTE2IDExLjc3MjYgNzMuNDc2OCAxMy4xNDc5IDc0Ljg1MjFDMTQuNTIzMSA3Ni4yMjc0IDE2LjM4ODQgNzcgMTguMzMzMyA3N0g2OS42NjY3QzcxLjY4MzMgNzcgNzMuNTI0IDc2LjE5MzMgNzQuODM2NyA3NC44MzY3IiBzdHJva2U9IiM5M0Q1RTUiIHN0cm9rZS13aWR0aD0iNCIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxwYXRoIGQ9Ik03NyA1NVYxOC4zMzMzQzc3IDE2LjM4ODQgNzYuMjI3NCAxNC41MjMyIDc0Ljg1MjEgMTMuMTQ3OUM3My40NzY4IDExLjc3MjYgNzEuNjExNiAxMSA2OS42NjY3IDExSDMzIiBzdHJva2U9IiM5M0Q1RTUiIHN0cm9rZS13aWR0aD0iNCIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPgo='
      }}
    />
  )
}

function Base64Image({
  imageUrl,
  mediaType,
  onLoad,
}: Pick<ImagePreviewModalProps, 'imageUrl' | 'mediaType'> & { onLoad: () => void }) {
  const imageSrc = `data:${mediaType};base64,${imageUrl}`

  return (
    <img
      src={imageSrc}
      alt={t`Preview`}
      style={{
        maxHeight: '100%',
        maxWidth: '100%',
        borderRadius: '6px',
        objectFit: 'contain',
      }}
      onLoad={onLoad}
      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.target as HTMLImageElement
        img.onerror = null
        img.src =
          'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHZpZXdCb3g9IjAgMCA4OCA4OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTcuMzMzMjUgNy4zMzMyNUw4MC42NjY2IDgwLjY2NjYiIHN0cm9rZT0iIzkzRDVFNSIgc3Ryb2tlLXdpZHRoPSI0IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHBhdGggZD0iTTM4LjE3IDM4LjE2OTlDMzcuNDg4NyAzOC44NTEyIDM2LjY3OTggMzkuMzkxNyAzNS43ODk2IDM5Ljc2MDRDMzQuODk5NCA0MC4xMjkyIDMzLjk0NTMgNDAuMzE5IDMyLjk4MTcgNDAuMzE5QzMyLjAxODEgNDAuMzE5IDMxLjA2NCA0MC4xMjkyIDMwLjE3MzggMzkuNzYwNEMyOS4yODM2IDM5LjM5MTcgMjguNDc0NyAzOC44NTEyIDI3Ljc5MzQgMzguMTY5OUMyNy4xMTIgMzcuNDg4NSAyNi41NzE2IDM2LjY3OTcgMjYuMjAyOCAzNS43ODk1QzI1LjgzNDEgMzQuODk5MiAyNS42NDQzIDMzLjk0NTEgMjUuNjQ0MyAzMi45ODE1QzI1LjY0NDMgMzIuMDE4IDI1LjgzNDEgMzEuMDYzOSAyNi4yMDI4IDMwLjE3MzZDMjYuNTcxNiAyOS4yODM0IDI3LjExMiAyOC40NzQ2IDI3Ljc5MzQgMjcuNzkzMiIgc3Ryb2tlPSIjOTNENUU1IiBzdHJva2Utd2lkdGg9IjQiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8cGF0aCBkPSJNNDkuNSA0OS41TDIyIDc3IiBzdHJva2U9IiM5M0Q1RTUiIHN0cm9rZS13aWR0aD0iNCIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxwYXRoIGQ9Ik02NiA0NEw3NyA1NSIgc3Ryb2tlPSIjOTNENUU1IiBzdHJva2Utd2lkdGg9IjQiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8cGF0aCBkPSJNMTMuMTYzMyAxMy4xNjMzQzEyLjQ3OTggMTMuODQgMTEuOTM2OCAxNC42NDUxIDExLjU2NTUgMTUuNTMyNEMxMS4xOTQyIDE2LjQxOTYgMTEuMDAyMSAxNy4zNzE1IDExIDE4LjMzMzNWNjkuNjY2N0MxMSA3MS42MTE2IDExLjc3MjYgNzMuNDc2OCAxMy4xNDc5IDc0Ljg1MjFDMTQuNTIzMSA3Ni4yMjc0IDE2LjM4ODQgNzcgMTguMzMzMyA3N0g2OS42NjY3QzcxLjY4MzMgNzcgNzMuNTI0IDc2LjE5MzMgNzQuODM2NyA3NC44MzY3IiBzdHJva2U9IiM5M0Q1RTUiIHN0cm9rZS13aWR0aD0iNCIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxwYXRoIGQ9Ik03NyA1NVYxOC4zMzMzQzc3IDE2LjM4ODQgNzYuMjI3NCAxNC41MjMyIDc0Ljg1MjEgMTMuMTQ3OUM3My40NzY4IDExLjc3MjYgNzEuNjExNiAxMSA2OS42NjY3IDExSDMzIiBzdHJva2U9IiM5M0Q1RTUiIHN0cm9rZS13aWR0aD0iNCIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPgo='
      }}
    />
  )
}

function GoogleDriveAssetPreview({ imageUrl, onLoad }: { imageUrl: string; onLoad: () => void }) {
  const googleDriveAssetUrl = getGoogleDriveAssetUrl(imageUrl)
  return (
    <iframe
      width="100%"
      height="100%"
      src={googleDriveAssetUrl}
      allowFullScreen
      onLoad={onLoad}
      title={t`Google Drive asset preview`}
      style={{
        borderRadius: '6px',
      }}
    />
  )
}
